import $ from "jquery";
import * as preact from "preact";
import * as _ from "underscore";

import { ajaxErrorMessage } from "ajaxErrorMessage";
import * as appleAuth from "appleAuth";
import type { SignInMethod } from "appStorePurchases";
import * as deviceDetect from "deviceDetect";
import * as googleAuth from "googleAuth";
import * as iOSBridge from "iOSBridge";
import * as localAPI from "localAPI";
import * as modalController from "modalController";
import readlangClient, { requestErrorMessage } from "readlangClient";
import * as languageMap from "shared/languageMap";
import * as utils from "shared/utils";
import validateEmail from "shared/validateEmail";
import type UserStore from "stores/UserStore";
import Button from "ui/Button";
import Dialog from "views/Dialog";
import * as walkthrough from "walkthrough";

type SignupMode = "initial" | "signup" | "signin";

interface SignupComponentProps {
  benefitsIllustration?: preact.ComponentChildren;
  // "signin" is for returning users, so it also skips the signup pitch.
  initialMode?: SignupMode;
  /** Signing in to this account (with only its ways of signing in) */
  email?: string;
  signInMethods?: SignInMethod[];
  stores: {
    user: UserStore;
  };
}

interface SignupComponentState {
  // On the website, once a click can open Apple's popup
  appleReady: boolean;
  email: string;
  errorMessage?: string;
  mode: SignupMode;
  password: string;
  submitting: boolean;
  username: string;
}

const redirectOrReload = () => {
  if (
    window.location.pathname === "/" ||
    window.location.pathname === "/login"
  ) {
    window.location.pathname = "/startSignedIn";
  } else {
    window.location.reload();
  }
};

class ForgotPasswordDialog extends preact.Component<
  { onComplete: () => void },
  { email: string }
> {
  private readonly emailInputRef = preact.createRef<HTMLInputElement>();

  constructor(props: { onComplete: () => void }) {
    super(props);
    this.state = { email: "" };
  }

  componentDidMount() {
    this.emailInputRef.current?.focus();
  }

  private submit = () => {
    $.ajax({
      type: "POST",
      url: "/api/forgotPassword",
      data: {
        email: this.state.email,
      },
      success: () => {
        this.props.onComplete();
        alert(
          "If an account exists with this email, you should receive an " +
            "email with a link to reset your password soon."
        );
      },
      error: (jqXHR) => {
        alert(ajaxErrorMessage(jqXHR));
      },
    });
  };

  private handleKeyPress = (event: KeyboardEvent) => {
    if (event.which === 10 || event.which === 13) {
      this.submit();
    }
  };

  render() {
    return (
      <Dialog
        heading="Reset your password"
        actions={
          <button id="forgotPasswordButton" onClick={this.submit}>
            Reset Password
          </button>
        }
      >
        <table class="tableForm">
          <tbody>
            <tr>
              <td>Email: </td>
              <td>
                <input
                  ref={this.emailInputRef}
                  type="email"
                  name="email"
                  autocomplete="username"
                  value={this.state.email}
                  onInput={(event) =>
                    this.setState({
                      email: (event.currentTarget as HTMLInputElement).value,
                    })
                  }
                  onKeyPress={this.handleKeyPress}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </Dialog>
    );
  }
}

export class SignupComponent extends preact.Component<
  SignupComponentProps,
  SignupComponentState
> {
  private googleSignInButtonElement?: HTMLElement;
  private readonly emailInputRef = preact.createRef<HTMLInputElement>();
  private readonly usernameInputRef = preact.createRef<HTMLInputElement>();
  private readonly passwordInputRef = preact.createRef<HTMLInputElement>();
  private socialSignInTimeout?: number;

  constructor(props: SignupComponentProps) {
    super(props);
    this.state = {
      appleReady: deviceDetect.isIOSApp(),
      email: props.email ?? "",
      mode: props.initialMode ?? "initial",
      password: "",
      submitting: false,
      username: "",
    };
  }

  componentDidMount() {
    (this.props.email
      ? this.passwordInputRef
      : this.emailInputRef
    ).current?.focus();
    this.attachSocialSignIn();
  }

  componentWillUnmount() {
    clearTimeout(this.socialSignInTimeout);
  }

  componentDidUpdate(
    _previousProps: SignupComponentProps,
    previousState: SignupComponentState
  ) {
    if (this.state.mode !== previousState.mode) {
      if (this.state.mode === "signup") {
        this.usernameInputRef.current?.focus();
      } else if (this.state.mode === "signin") {
        this.emailInputRef.current?.focus();
      } else {
        this.setFocus();
      }
    }
  }

  setFocus() {
    this.googleSignInButtonElement?.focus();
  }

  private attachSocialSignIn() {
    this.socialSignInTimeout = window.setTimeout(async () => {
      if (!this.googleSignInButtonElement) {
        return;
      }

      googleAuth.attachSignInClickHandler(
        this.googleSignInButtonElement,
        {},
        this.completeSocialSignIn,
        () => {
          console.error("Google login fail");
        }
      );
      this.setState({ appleReady: await appleAuth.prepare() });
    }, 50);
  }

  private completeSocialSignIn = () => {
    localAPI.pushSync(
      readlangClient.remoteRequest.bind(readlangClient),
      async () => {
        await localAPI.setLocalMode(false);
        redirectOrReload();
      }
    );
  };

  // In the iOS app, Apple and Google sign in natively: Google refuses its web
  // sign-in inside an app's web view. On the website, Apple signs in with its
  // popup, and Google with its own button.
  private signInWith = async (provider: "apple" | "google") => {
    const bridge = iOSBridge.get();
    this.setState({ errorMessage: undefined });
    try {
      if (provider === "apple") {
        // The website's popup opens at once, while the click allows it to
        await readlangClient.request(
          "POST",
          "/api/auth/appleSignIn",
          bridge ? await bridge.signInWithApple() : await appleAuth.authorize()
        );
      } else if (bridge) {
        const { idToken } = await bridge.signInWithGoogle();
        await readlangClient.request("POST", "/api/auth/googleSignIn", {
          idToken,
        });
      }
    } catch (error) {
      if (!error.cancelled) {
        this.setState({
          // e.g. an existing account whose address isn't verified
          errorMessage:
            error.jqXHR?.data?.errorMessage ??
            "Sign in failed, please try again",
        });
      }
      return;
    }
    this.completeSocialSignIn();
  };

  private validationError() {
    if (this.state.email.length === 0) {
      return "Please enter your email address";
    }
    if (!validateEmail(this.state.email)) {
      return "Invalid email address: " + this.state.email;
    }
    if (this.state.password.length === 0) {
      return "Please choose a password";
    }
    if (this.state.password.length < 5) {
      return "Your password must be at least 5 characters";
    }
    return undefined;
  }

  private shouldConfirmCommonDomainTypo() {
    if (this.state.mode === "signin") {
      return false;
    }

    const domain = this.state.email.split("@")[1];
    return !_.every(
      [
        "gmail.com",
        "yahoo.com",
        "hotmail.com",
        "gmx.de",
        "googlemail.com",
        "mail.ru",
      ],
      (possible) => utils.levenshteinDistance(domain, possible) !== 1
    );
  }

  private createAccount = async () =>
    new Promise((resolve, reject) => {
      readlangClient.request(
        "POST",
        "/api/user",
        {
          displayName: this.state.username,
          email: this.state.email,
          password: this.state.password,
          firstLanguage: this.props.stores.user.firstLanguage.value,
          currentLearningLanguage:
            this.props.stores.user.currentLearningLanguage.value,
        },
        (response) => {
          if (response.errorMessage) {
            reject(response.errorMessage);
          } else if (response.success) {
            resolve(response);
          } else {
            console.error("didn't understand response");
            reject("didn't understand response");
          }
        },
        (errorResponse) => {
          if (errorResponse.alreadyNotified) {
            // Terminal failure (network loss, 5xx): readlangClient already
            // showed a notification bar, and `data` may be raw HTML — keep
            // the inline message simple.
            reject("Connection problem. Please try again.");
          } else if (errorResponse.data) {
            reject(
              errorResponse.data.errorMessage ??
                requestErrorMessage({ jqXHR: errorResponse })
            );
          } else {
            reject("Unknown error");
          }
        },
        { https: true }
      );
    });

  private login = async () =>
    new Promise((resolve, reject) => {
      $.ajax({
        type: "POST",
        // Use the page's own scheme. Hard-coding https for non-localhost hosts
        // broke login over plain http on LAN/dev hosts — e.g. the iOS app on a
        // physical device loading the Mac's `.local` host.
        url:
          window.location.protocol + "//" + window.location.host + "/api/login",
        data: {
          email: this.state.email,
          password: this.state.password,
          rememberMe: true,
          firstLanguage: this.props.stores.user.firstLanguage.value,
          currentLearningLanguage:
            this.props.stores.user.currentLearningLanguage.value,
        },
        xhrFields: {
          withCredentials: true,
        },
        success: (data) => {
          if (typeof data === "string") {
            data = JSON.parse(data);
          }
          if ("success" in data) {
            localAPI.pushSync(
              readlangClient.remoteRequest.bind(readlangClient),
              async () => {
                await localAPI.setLocalMode(false);
                redirectOrReload();
              }
            );
            resolve(data);
          } else {
            reject("Email or password not recognized, please try again");
          }
        },
        error: (jqXHR) => {
          reject(
            ajaxErrorMessage(
              jqXHR,
              "Email or password not recognized, please try again"
            )
          );
        },
      });
    });

  private submit = async () => {
    const errorMessage = this.validationError();

    if (errorMessage) {
      this.setState({ errorMessage });
      return;
    }

    if (this.shouldConfirmCommonDomainTypo()) {
      const domain = this.state.email.split("@")[1];
      if (
        !confirm(
          'It looks like you may have mis-typed your email address, are you sure your email ends with "' +
            domain +
            '"?'
        )
      ) {
        return;
      }
    }

    this.setState({ errorMessage: undefined, submitting: true });

    try {
      if (this.state.mode !== "signin") {
        await this.createAccount();
      }
      await this.login();
    } catch (error) {
      this.setState({
        errorMessage: String(error),
        submitting: false,
      });
    }
  };

  private startSignupMode = () => {
    if (this.state.mode === "signup") {
      void this.submit();
      return;
    }
    this.setState({ errorMessage: undefined, mode: "signup" });
  };

  private startSignInMode = () => {
    if (this.state.mode === "signin") {
      void this.submit();
      return;
    }
    this.setState({ errorMessage: undefined, mode: "signin" });
  };

  private showAllMethods = () => {
    this.setState({ errorMessage: undefined, mode: "initial" });
  };

  private showForgotPasswordDialog = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    modalController.show(
      <ForgotPasswordDialog
        onComplete={() => {
          modalController.cancel();
        }}
      />
    );
  };

  private handlePasswordKeyPress = (event: KeyboardEvent) => {
    if (event.which === 10 || event.which === 13) {
      void this.submit();
    }
  };

  render() {
    const languageString = walkthrough.hasAchievement("setLanguages")
      ? languageMap.map[this.props.stores.user.currentLearningLanguage.value]
          .english
      : "";
    const canSignInWith = (method: SignInMethod) =>
      !this.props.signInMethods || this.props.signInMethods.includes(method);
    // Picking an email option from the full list of ways to sign in narrows
    // the form to it, with a way back to the list.
    const choseEmail =
      this.state.mode !== (this.props.initialMode ?? "initial");
    const showEmailForm =
      this.state.mode !== "initial" && canSignInWith("password");
    const showName = this.state.mode === "signup";

    let signInLabel = "Sign in with email";
    if (this.state.mode === "signin") {
      signInLabel = this.state.submitting ? "Signing in..." : "Sign in";
    }

    let createAccountLabel = "Create new account with email";
    if (this.state.mode === "signup") {
      createAccountLabel = this.state.submitting
        ? "Creating account..."
        : "Create new account";
    }

    return (
      <div id="signUpForm" data-testid="signup-form">
        {choseEmail && (
          <Button
            kind="plain"
            icon="icon-chevron-left"
            class="signupBackButton"
            disabled={this.state.submitting}
            onClick={this.showAllMethods}
          >
            Other sign-in options
          </Button>
        )}

        {this.props.initialMode !== "signin" && !choseEmail && (
          <>
            <p>Read and learn {languageString} faster than ever before</p>

            <div
              class={utils.cn({
                signupBenefits: true,
                illustrated: !!this.props.benefitsIllustration,
              })}
            >
              <ul class="bullets">
                <li>Get help with new vocab</li>
                <li>Review with flashcards</li>
                <li>Import books, articles or webpages</li>
                {/* The app is already there */}
                {!deviceDetect.isIOSApp() && (
                  <li>Use on your phone or tablet</li>
                )}
              </ul>
              {this.props.benefitsIllustration}
            </div>
          </>
        )}

        {/* Collapsed rather than removed, to keep Google's rendered button,
            and rather than display:none, so it keeps the width Google renders
            to if its script loads late */}
        <div
          class={utils.cn({ signupSocialButtons: true, collapsed: choseEmail })}
        >
          {canSignInWith("apple") && this.state.appleReady && (
            <button
              class="payButton socialSignInButton appleSignInButton"
              disabled={this.state.submitting}
              onClick={() => this.signInWith("apple")}
            >
              <img src="/content/apple-logo-button.png" alt="" /> Continue with
              Apple
            </button>
          )}
          {deviceDetect.isIOSApp() ? (
            canSignInWith("google") && (
              <button
                class="payButton socialSignInButton nativeGoogleSignInButton"
                disabled={this.state.submitting}
                onClick={() => this.signInWith("google")}
              >
                <img src="/content/google-g-logo.svg" alt="" /> Continue with
                Google
              </button>
            )
          ) : (
            <button
              class="googleSignInButton payButton"
              disabled={this.state.submitting}
              // Google's iframe inside ignores the disabled attribute, so stop
              // it taking clicks and focus too
              inert={this.state.submitting}
              ref={(element) => {
                if (element) {
                  this.googleSignInButtonElement = element;
                }
              }}
            />
          )}
        </div>

        {this.state.errorMessage && (
          <p class="signupErrorMessage errorMessage">
            {this.state.errorMessage}
          </p>
        )}

        {showEmailForm && (
          <table class="emailForm signupEmailForm">
            <tbody>
              {showName && (
                <tr class="nameRow">
                  <td>Name</td>
                  <td>
                    <label>
                      <input
                        ref={this.usernameInputRef}
                        type="text"
                        name="username1"
                        autocomplete="name"
                        data-testid="signup-username-input"
                        value={this.state.username}
                        onInput={(event) =>
                          this.setState({
                            username: (event.currentTarget as HTMLInputElement)
                              .value,
                          })
                        }
                      />
                    </label>
                  </td>
                </tr>
              )}
              <tr>
                <td>Email</td>
                <td>
                  <label>
                    <input
                      ref={this.emailInputRef}
                      type="email"
                      name="email1"
                      autocomplete="username"
                      data-testid="signup-email-input"
                      value={this.state.email}
                      onInput={(event) =>
                        this.setState({
                          email: (event.currentTarget as HTMLInputElement)
                            .value,
                        })
                      }
                    />
                  </label>
                </td>
              </tr>
              <tr>
                <td>Password</td>
                <td>
                  <label>
                    <input
                      ref={this.passwordInputRef}
                      type="password"
                      name="password1"
                      autocomplete={
                        this.state.mode === "signin"
                          ? "current-password"
                          : "new-password"
                      }
                      data-testid="signup-password-input"
                      value={this.state.password}
                      onInput={(event) =>
                        this.setState({
                          password: (event.currentTarget as HTMLInputElement)
                            .value,
                        })
                      }
                      onKeyPress={this.handlePasswordKeyPress}
                    />
                  </label>
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {this.state.mode !== "signin" && (
          <Button
            kind="primary"
            size="large"
            block
            class="signupEmailButton"
            id="newAccountButton"
            data-testid="signup-create-account-button"
            disabled={this.state.submitting}
            onClick={this.startSignupMode}
          >
            {createAccountLabel}
          </Button>
        )}

        {this.state.mode === "initial" && (
          <p class="emailLoginNotice signupEmailLoginNotice">
            Already signed up by email?
          </p>
        )}

        {this.state.mode !== "signup" && canSignInWith("password") && (
          <Button
            // Under the green button to create an account, until signing in
            kind={this.state.mode === "signin" ? "primary" : "secondary"}
            size="large"
            block
            class="signupEmailButton"
            id="signInFormButton"
            data-testid="signup-sign-in-button"
            disabled={this.state.submitting}
            onClick={this.startSignInMode}
          >
            {signInLabel}
          </Button>
        )}

        {this.state.mode === "signin" && canSignInWith("password") && (
          <div class="forgotPasswordContainer signupForgotPasswordContainer">
            Forgot Password?{" "}
            <a
              href="#"
              id="forgotPassword"
              onClick={this.showForgotPasswordDialog}
            >
              Click here to reset
            </a>
          </div>
        )}
      </div>
    );
  }
}
